import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../../config/config";
import { toast } from "react-toastify";
import { languageHelper } from "../../../config/helperFunctions";
import SelectReact from "../../../components/Select/SelectReact";

function CopyOutletItems() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [outlets, setOutlets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);

  // Form State
  const [sourceOutlet, setSourceOutlet] = useState(null);
  const [targetOutlet, setTargetOutlet] = useState(null);
  const [copyType, setCopyType] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch Outlets on mount
  useEffect(() => {
    setPageLoading(true);
    axios
      .get(`${apiKey}/api/rms/admin/get-outlets`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setOutlets(
          res.data.map((outlet) => ({
            value: outlet.id,
            label: `${outlet.code} - ${outlet.name}`,
          }))
        );
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to load outlets");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [user?.token]);

  // Fetch categories when sourceOutlet changes
  useEffect(() => {
    if (!sourceOutlet) {
      setCategories([]);
      return;
    }
    if (copyType === "category") {
      axios
        .get(`${apiKey}/api/rms/admin/categories`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
          params: {
            outletId: sourceOutlet,
          },
        })
        .then((res) => {
          setCategories(
            res.data.map((cat) => ({
              value: cat.id,
              label: cat.category_name,
            }))
          );
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [sourceOutlet, copyType, user?.token]);

  // Fetch items when sourceOutlet changes
  useEffect(() => {
    if (!sourceOutlet) {
      setItems([]);
      return;
    }
    if (copyType === "item") {
      axios
        .get(`${apiKey}/api/rms/admin/foodMenus`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
          params: {
            outletId: sourceOutlet,
          },
        })
        .then((res) => {
          // FoodMenus endpoint might return { foodMenus: [...] }
          const menuList = res.data.foodMenus || res.data || [];
          setItems(
            menuList.map((item) => ({
              value: item.id,
              label: `${item.code} - ${item.name}`,
            }))
          );
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [sourceOutlet, copyType, user?.token]);

  const handleSourceSelect = (option) => {
    setSourceOutlet(option ? option.value : null);
    setSelectedCategory(null);
    setSelectedItem(null);
    // Reset target if it is the same as source
    if (option && targetOutlet === option.value) {
      setTargetOutlet(null);
    }
  };

  const handleTargetSelect = (option) => {
    setTargetOutlet(option ? option.value : null);
  };

  const handleCopyTypeSelect = (option) => {
    setCopyType(option ? option.value : "all");
    setSelectedCategory(null);
    setSelectedItem(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sourceOutlet || !targetOutlet) {
      toast.warn("Please select both source and target outlets.");
      return;
    }

    setLoading(true);
    const payload = {
      source_outlet_id: sourceOutlet,
      target_outlet_id: targetOutlet,
      type: copyType,
      category_id: copyType === "category" ? selectedCategory : null,
      item_id: copyType === "item" ? selectedItem : null,
    };

    axios
      .post(`${apiKey}/api/rms/admin/outlets/copy-data`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        toast.success(res.data.message || "Data copied successfully!");
        setLoading(false);
        navigate(-1);
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.message || "Failed to copy menu items"
        );
        setLoading(false);
      });
  };

  const copyTypeOptions = [
    { value: "all", label: languageHelper("All Items") || "All Items" },
    { value: "category", label: languageHelper("Specific Category") || "Specific Category" },
    { value: "item", label: languageHelper("Specific Item") || "Specific Item" },
    { value: "modifiers", label: languageHelper("All Modifiers") || "All Modifiers" },
  ];

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex border-b border-gray-200 pb-4 mb-6">
          <h1 className="text-d-bg-text text-2xl font-bold">
            {languageHelper("Copy Items Between Branches") || "Copy Items Between Branches"}
          </h1>
          <button
            className="bg-d-cat-btn text-white text-sm px-4 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 text-center ms-auto"
            onClick={() => navigate(-1)}
          >
            {languageHelper("Back") || "Back"}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source Outlet */}
            <label className="w-full block">
              <span className="text-d-bg-text text-sm mb-2 font-bold block">
                {languageHelper("Source Outlet") || "Source Outlet"}
              </span>
              <SelectReact
                options={outlets}
                onChange={handleSourceSelect}
                placeholder={languageHelper("Select Source Outlet") || "Select Source Outlet"}
              />
            </label>

            {/* Target Outlet */}
            <label className="w-full block">
              <span className="text-d-bg-text text-sm mb-2 font-bold block">
                {languageHelper("Target Outlet") || "Target Outlet"}
              </span>
              <SelectReact
                options={outlets.filter((o) => o.value !== sourceOutlet)}
                onChange={handleTargetSelect}
                placeholder={languageHelper("Select Target Outlet") || "Select Target Outlet"}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Copy Type */}
            <label className="w-full block">
              <span className="text-d-bg-text text-sm mb-2 font-bold block">
                {languageHelper("Copy Type") || "Copy Type"}
              </span>
              <SelectReact
                options={copyTypeOptions}
                onChange={handleCopyTypeSelect}
                defaultValue={copyTypeOptions[0]}
              />
            </label>

            {/* Conditional Dropdowns based on copyType */}
            {copyType === "category" && sourceOutlet && (
              <label className="w-full block">
                <span className="text-d-bg-text text-sm mb-2 font-bold block">
                  {languageHelper("Select Category") || "Select Category"}
                </span>
                <SelectReact
                  options={categories}
                  onChange={(opt) => setSelectedCategory(opt ? opt.value : null)}
                  placeholder={languageHelper("Select Category") || "Select Category"}
                />
              </label>
            )}

            {copyType === "item" && sourceOutlet && (
              <label className="w-full block">
                <span className="text-d-bg-text text-sm mb-2 font-bold block">
                  {languageHelper("Select Item") || "Select Item"}
                </span>
                <SelectReact
                  options={items}
                  onChange={(opt) => setSelectedItem(opt ? opt.value : null)}
                  placeholder={languageHelper("Select Item") || "Select Item"}
                />
              </label>
            )}
          </div>

          <div className="border-t border-gray-200 pt-6 mt-6 flex justify-end">
            <button
              type="submit"
              disabled={loading || !sourceOutlet || !targetOutlet}
              className={`bg-d-cat-btn text-white font-bold text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 text-center ${
                (loading || !sourceOutlet || !targetOutlet) && "opacity-50 cursor-not-allowed"
              }`}
            >
              {loading
                ? languageHelper("Copying...") || "Copying..."
                : languageHelper("Start Copy") || "Start Copy"}
            </button>
          </div>
        </form>
      </div>

      {pageLoading && (
        <div className="absolute top-0 left-0 w-full h-full grid place-items-center bg-white bg-opacity-70 z-50">
          <img src="/assets/gif/dash2.gif" alt="Loading" />
        </div>
      )}
    </main>
  );
}

export default CopyOutletItems;
